import { useState, useRef, useEffect } from "react"
import { message } from "antd"
import {
  WIZARD_STAGE,
  validateFreeTextAnswer,
  validateGradeFreeText,
  validatePopulationCount,
  detectPopulationFieldMention,
  schoolGradeKeys,
  schoolGradePopulations,
} from "./constants"
import persistMessage from "../chatMessagePersistence"
import {
  buildTextMessage,
  buildAnalyzingMessage,
  buildErrorMessage,
  buildFoundActivitiesMessage,
  buildStartPromptMessage,
  buildResumePendingActivityMessage,
  buildActivityBannerMessage,
  buildConfirmMatchMessage,
  buildDomainQuestionMessage,
  buildTierQuestionMessage,
  buildGradesQuestionMessage,
  buildGradesProgressCardMessage,
  buildAddAnotherGradeMessage,
  buildUnmatchedGradesInfoMessage,
  buildYearsQuestionMessage,
  buildNameDescriptionMessage,
  buildDescriptionQuestionMessage,
  buildDataCollectionQuestionMessage,
  buildIndicatorsQuestionMessage,
  buildSummaryMessage,
  buildSuccessMessage,
  buildIncompleteExistingActivitiesMessage,
  buildTypoConfirmationMessage,
} from "./wizardMessageBuilders"

const POLL_INTERVAL_MS = 3000
const WIZARD_STATE_KEY = "program_matrix_wizard_state"

// Mirrors ChatBotUI's Program Matrix accept filter (".csv,.xls,.xlsx") — off-page/on-page attach
// no longer restricts the OS file picker by extension (so a cross-module redirect is possible),
// so a non-CSV/Excel file can reach here and needs to be rejected with a clear message instead of
// silently failing analysis.
const ALLOWED_EXTENSIONS = [".csv", ".xls", ".xlsx"]
const ALLOWED_MIME_TYPES = [
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]

const isValidFileType = (file) => {
  if (file.type && ALLOWED_MIME_TYPES.includes(file.type.toLowerCase())) return true
  const ext = `.${file.name.split(".").pop().toLowerCase()}`
  return ALLOWED_EXTENSIONS.includes(ext)
}

// Fields with a real, validatable value behind them (checked via validateFreeTextAnswer) — as
// opposed to workflow/navigation steps (start, confirm_match, summary) or fields with their own
// dedicated multi-value widget (grades, indicators), where a typed chat message has no sensible
// single-value interpretation. Whenever one of these is the currently open question, typing
// directly into the chat box answers it — see currentFieldRef below — rather than requiring the
// "type a different X" button first.
const FREE_TEXT_ELIGIBLE_FIELDS = [
  "domain",
  "tier",
  "years",
  "name_description",
  "description",
  "data_collection",
]

// "grades" also captures direct typed input (see submitGradeFreeText) but resolves through its
// own match-against-document-or-dashboard logic rather than the generic single-value
// substitution FREE_TEXT_ELIGIBLE_FIELDS use — kept as a separate list so submitFreeText can
// still branch to the right handler, while emitFieldQuestion treats both lists the same way for
// deciding whether a question is currently "open" to typed input at all.
//
// "start" is the same story as "grades": the "Start with Activity 1 →" button can only ever
// start the first activity, so a typed "activity 2" (see submitStartFreeText) is the only way to
// jump straight to a later one — without it here, that typed answer falls through to the
// generic GPT chat endpoint instead of the wizard, which has no concept of "activity 2" at all.
//
// "existing_incomplete_summary"/"summary" are plain binary (yes-or-no / save-or-restart)
// single_choice questions with no free-text option of their own at all — without including them
// here too, a typed reply like "yes" falls through to the generic GPT chat endpoint the exact
// same way, which has no awareness of this activity's pending state and will happily "chat"
// through a fake version of the same exchange (see submitExistingIncompleteSummaryFreeText/
// submitSummaryFreeText) without ever applying real validation or saving anything.
//
// "typo_confirm" is the "did you mean X?" prompt validateFreeTextAnswer's matchAgainstList offers
// instead of flatly rejecting a near-miss typed answer (see buildTypoConfirmationMessage) — same
// reasoning again: without it here, a typed "yes"/"no" reply to THAT prompt would itself fall
// through to the generic chat endpoint instead of resolving it.
//
// "grades_add_another" is the same story once more, for the "Would you like to add another
// grade?" prompt shown after a grade's population is fully captured (see
// buildAddAnotherGradeMessage/resolveAddAnotherGrade).
// "indicators" is here for the same reason as the others — without it, a typed reply to the SEL
// indicators checklist (a multi-select widget with per-code connection-type/explanation sub-state,
// not a simple single value) falls through to the generic GPT chat endpoint, which has no
// awareness of this activity's actual candidate indicators and will freely invent a completely
// different, fabricated set (wrong codes, wrong descriptions) rather than reflecting what's really
// on screen. Real free-text add/remove-by-typing isn't implemented for this field (it needs
// bespoke multi-value parsing on par with submitGradeFreeText) — submitIndicatorsFreeText just
// redirects back to the real checklist buttons instead of either guessing or letting the fake
// GPT conversation happen.
const TYPED_INPUT_FIELDS = [
  ...FREE_TEXT_ELIGIBLE_FIELDS,
  "grades",
  "start",
  "existing_incomplete_summary",
  "summary",
  "typo_confirm",
  "grades_add_another",
  "indicators",
  "resume_activity",
]

// A typed answer to the "start" prompt with no activity number reads the same way clicking the
// button would — start from Activity 1 (see submitStartFreeText).
const AFFIRMATIVE_START_WORDS = ["yes", "yeah", "yep", "sure", "ok", "okay", "ready", "start", "go"]

// "name_description"/"description"/"data_collection" are free-form fields where
// validateFreeTextAnswer accepts any non-blank text as a valid value, with no vocabulary check to
// catch it — so a typed reply that's clearly REFERRING to one of the question's own on-screen
// buttons ("yes", "keep document", the button's own label, ...) must be read as choosing that
// button's value, not literally saved as the name/description/data text itself. Matched against
// the CURRENT question's actual button labels (see currentQuestionOptions below) rather than a
// hardcoded word list per field — these questions can offer very different option sets depending
// on the document/AI data (a plain "Yes, use this", or a two-way "Keep my name (X)" vs "Use Rei's
// shorter version (Y)" choice with no "yes" wording at all), and reading straight off what's
// actually rendered can never drift out of sync with a builder's own branching logic the way a
// separately-maintained phrase list could. Same "don't guess" rule matchAgainstList's own partial
// match uses: exact label match first, then a partial (substring either direction) match ONLY
// when it's unambiguous — two options both loosely matching (e.g. a bare "yes" against a two-way
// choice where neither button says "yes") correctly resolves to no match, falling through to the
// literal-free-text path since that's a genuine "type your own answer" case, not a redirect.
// freeText options (their label is an instruction like "Edit - Please type a response.", not a
// real value) are never matched — typing IS how you answer those, there's nothing to redirect to.
const matchOptionByLabel = (options, text) => {
  const selectable = (options || []).filter((opt) => !opt.freeText)
  const lowerText = text.trim().toLowerCase()
  const exact = selectable.find((opt) => opt.label.toLowerCase() === lowerText)
  if (exact) return exact
  const partial = selectable.filter((opt) => {
    const label = opt.label.toLowerCase()
    return label.includes(lowerText) || lowerText.includes(label)
  })
  return partial.length === 1 ? partial[0] : null
}

// Loosely reads typed text as one of two named intents (e.g. "add" vs "skip") via simple keyword
// matching, for a binary single_choice-shaped question whose two buttons already carry
// unambiguous, distinct wording — same idea as AFFIRMATIVE_START_WORDS, generalized to any
// yes/no-shaped pair. positiveWords is checked first so a reply like "yes add" (which contains
// both a generic "yes" and the specific action word) resolves to whichever specific word it
// actually names, rather than a leading "yes" alone deciding it.
const matchBinaryIntent = (text, positiveWords, negativeWords) => {
  const normalized = text.trim().toLowerCase()
  if (positiveWords.some((word) => normalized.includes(word))) return "positive"
  if (negativeWords.some((word) => normalized.includes(word))) return "negative"
  return null
}

// The SAME per-grade population lookup GradePicker's own initial `counts` seed uses (see
// buildGradesQuestionMessage/GradePicker.jsx's own `pick`) — a per-grade breakdown from the
// document (suggested_grade_populations) first, falling back to one aggregate total for the whole
// activity (suggested_students_can_be_supported/_being_supported) only when the per-grade
// breakdown doesn't cover that grade/field. Replicated here (rather than imported) since it's
// only a few lines and wizardMessageBuilders.js already owns the widget-facing version — needed
// so a grade folded in here via chat (bypassing the widget) still gets the SAME document-provided
// counts the widget itself would have shown pre-filled, instead of starting blank and asking the
// user for data the document already gave. Returns null for a field/grade the document doesn't
// cover — that's genuinely missing, not just unfetched, so the chat-driven ask still applies.
const documentPopulationForGrade = (activity, grade) => {
  const fromGrade = (activity.suggested_grade_populations || {})[grade]
  const pick = (field) => {
    if (fromGrade && fromGrade[field] != null) return fromGrade[field]
    const aggregate = activity[`suggested_students_${field}`]
    return Number.isFinite(aggregate) ? aggregate : null
  }
  return {
    can_be_supported: pick("can_be_supported"),
    being_supported: pick("being_supported"),
  }
}

// ChatBotLauncher only renders ChatBotUI while the drawer is open, so closing it (the "X"
// button) unmounts this hook entirely — without this, extractedActivities/activityIndex/
// collectedByRef would reset to empty and reopening would leave a restored, still-interactive
// question with nothing behind it to resolve it against.
const loadPersistedWizardState = () => {
  try {
    const raw = localStorage.getItem(WIZARD_STATE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch (e) {
    return {}
  }
}

const emptyCollected = () => ({
  domain: null,
  tier: null,
  grades: [],
  internalSelectedGrades: [],
  // Document grades explicitly skipped via the one-at-a-time unmatched-grades prompt (or the
  // "no, skip the rest" gate) — tracked separately from `grades` (which only ever holds grades
  // actually INCLUDED) so a skipped grade doesn't keep re-appearing as "still pending" on every
  // later pending-grades check. Never sent to the backend — saveActivity's itemParams only reads
  // specific named fields off `collected`, not a blanket spread, so this is safe to carry here.
  excludedGrades: [],
  years: null,
  name: null,
  description: null,
  dataCollection: null,
  indicators: [],
  sel_standards: {},
})

// Drives the Program Matrix AI Coach chat wizard: kicks off document extraction, polls for
// results, walks the user through one field at a time per activity (branching into the
// keep/accept/pick-new score-review widgets whenever ProgramMatrixActivityMatcher found an
// existing match), and saves each completed activity via the existing
// PUT program_matrix_items/:id endpoint.
const useProgramMatrixWizard = ({ isActive, setMessages, messages }) => {
  // Read once per mount (not on every render) — this is only ever the initial useState value.
  const persistedStateRef = useRef(null)
  if (persistedStateRef.current === null) {
    persistedStateRef.current = loadPersistedWizardState()
  }
  const persisted = persistedStateRef.current

  const [stage, setStage] = useState(persisted.stage || WIZARD_STAGE.IDLE)
  const [extractedActivities, setExtractedActivities] = useState(
    persisted.extractedActivities || [],
  )
  const [activityIndex, setActivityIndex] = useState(
    persisted.activityIndex || 0,
  )
  const [collectedByRef, setCollectedByRef] = useState(
    persisted.collectedByRef || {},
  )
  const [awaitingFreeText, setAwaitingFreeText] = useState(null)
  const [isBusy, setIsBusy] = useState(false)
  // Mutating currentFieldRef.current alone (a plain ref) never triggers a re-render on its own —
  // fine everywhere ELSE it's set, since that always happens inside a handler that's already
  // about to re-render for some other reason (pushMessage's setMessages, setCollectedByRef, ...).
  // The rehydration effect below is the one exception: it runs standalone, with nothing else
  // already re-rendering to carry the ref's new value into the returned isAwaitingFreeText — so
  // it bumps this counter itself to force that re-render explicitly.
  const [, forceRerenderForRehydration] = useState(0)
  const pollRef = useRef(null)
  // The { activityRef, field } behind whatever free-text-eligible question is currently on
  // screen and unanswered, kept in sync by emitFieldQuestion (set/cleared on every question it
  // emits) plus an explicit reset wherever a message is pushed WITHOUT going through
  // emitFieldQuestion (startActivity, handleFileAttach) so it never points at a stale field from
  // a previous question. A ref rather than state — it only needs to be read at the moment a chat
  // message is submitted (submitFreeText), not to trigger a re-render on its own.
  const currentFieldRef = useRef(null)
  // Drives the "collect a grade's population via chat" sub-flow (see
  // maybeStartPopulationCollection/submitPopulationFreeText) — the ONE mechanism every typed grade
  // (school-configured or out-of-config) walks through to fill in its can/being-supported counts,
  // one grade and one value at a time. Shape: { activityRef, queue: [grade, ...still to ask],
  // currentGrade, stage: "can" | "being", canValue }. Non-null exactly while
  // currentFieldRef.current.field === "grade_population".
  const pendingPopulationRef = useRef(null)
  // The { activityRef, field, suggestion } behind a typo-confirmation prompt currently on screen
  // (see buildTypoConfirmationMessage/resolveTypoConfirmation) — `field` here is the ORIGINAL
  // question's field (e.g. "domain"), not "typo_confirm" itself, so "yes" can apply `suggestion`
  // to the right field and "no" can re-ask the right original question. Non-null exactly while
  // currentFieldRef.current.field === "typo_confirm".
  const pendingTypoConfirmationRef = useRef(null)
  // Tracks which extraction the wizard is currently interested in, independent of pollRef —
  // needed because pollRef alone can't tell a NEW poll's response apart from an OLD one still
  // in flight (see pollExtraction below).
  const activeExtractionIdRef = useRef(null)
  // Whether extractedActivities currently holds the "fill in existing incomplete matrix items"
  // queue rather than a document's own extracted activities — refs, not state, since they only
  // ever gate behavior inside callbacks and don't need to trigger a re-render themselves.
  const isExistingItemModeRef = useRef(false)
  const incompleteByCategoryRef = useRef({})
  // client_refs of every activity in the CURRENT extractedActivities batch that's actually been
  // saved (see saveActivity's putItem success callback) — needed because startActivity(index) no
  // longer walks activities in strict order: submitStartFreeText lets the user jump straight to
  // any activity by number or name (e.g. start with #2 in a 2-activity document), so
  // "activityIndex + 1 is out of bounds" no longer means "every activity is done" — an earlier
  // one the user skipped past can still be genuinely untouched. Reset (new Set()) whenever a new
  // batch starts — a new document upload (handleFileAttach) or the "help me fill these in"
  // existing-items queue (respondToExistingIncompleteSummary) — since client_refs are index-based
  // ("extract-0", "extract-1", ...) and would otherwise collide with a PREVIOUS batch's refs at
  // the same position.
  const savedActivityRefsRef = useRef(new Set())

  useEffect(() => {
    if (!isActive) return
    try {
      localStorage.setItem(
        WIZARD_STATE_KEY,
        JSON.stringify({
          stage,
          extractedActivities,
          activityIndex,
          collectedByRef,
        }),
      )
    } catch (e) {
      // Best-effort — losing resume-on-reopen (e.g. quota exceeded) shouldn't break the wizard.
    }
  }, [isActive, stage, extractedActivities, activityIndex, collectedByRef])

  // Rehydrates currentFieldRef from the restored `messages` themselves whenever this hook mounts
  // (or messages first arrive) with nothing already tracked — closing and reopening the chat
  // drawer unmounts ChatBotUI (and this hook) entirely, so currentFieldRef/awaitingFreeText/
  // pendingPopulationRef all reset to null even though `messages` (owned by ChatBotLauncher,
  // restored from localStorage) still shows the exact same still-unanswered question with its
  // buttons still clickable. Without this, that visually "open" question would silently stop
  // accepting typed answers — falling through to the generic chatbot — right after a reopen, with
  // nothing on screen suggesting anything had changed. Only recovers a question that carries real
  // wizardMeta (every single_choice field question does); a plain-text grade_population sub-flow
  // prompt has no field/activityRef to recover from, so a population sub-flow interrupted mid-way
  // by a remount is a known, narrower gap this doesn't cover.
  useEffect(() => {
    if (!isActive) return
    if (currentFieldRef.current) return
    const lastOpen = [...(messages || [])].reverse().find(
      (m) => !m.answered && m.wizardMeta && TYPED_INPUT_FIELDS.includes(m.wizardMeta.field),
    )
    if (lastOpen) {
      currentFieldRef.current = {
        activityRef: lastOpen.wizardMeta.activityRef,
        field: lastOpen.wizardMeta.field,
      }
      // typo_confirm's OWN field/activityRef point at the confirmation prompt itself, not the
      // original question it's confirming a value for — pendingTypoConfirmationRef needs that
      // original field/suggestion too (carried in wizardMeta for exactly this reason) or "yes"/
      // "no" would have nothing to resolve after a remount.
      if (lastOpen.wizardMeta.field === "typo_confirm") {
        pendingTypoConfirmationRef.current = {
          activityRef: lastOpen.wizardMeta.activityRef,
          field: lastOpen.wizardMeta.originalField,
          suggestion: lastOpen.wizardMeta.suggestion,
        }
      }
      // Forces the re-render that picks up the ref's new value in isAwaitingFreeText — see the
      // note on forceRerenderForRehydration above.
      forceRerenderForRehydration((n) => n + 1)
    }
  }, [isActive, messages])

  if (!isActive) {
    return {
      stage: WIZARD_STAGE.IDLE,
      handleFileAttach: () => {},
      onWizardRespond: () => {},
      submitFreeText: () => false,
      isBusy: false,
      isAwaitingFreeText: false,
    }
  }

  const pushMessage = (msg) => {
    setMessages((prev) => [...prev, msg])
    persistMessage(msg)
  }

  // The real, currently-rendered options for the open `field`/`activityRef` question — read
  // straight off the actual pushed message (see matchOptionByLabel above for why) rather than
  // re-derived from `activity`. Only the LAST matching unanswered message counts, same as
  // markFieldQuestionAnswered's own notion of "the" open question for a field.
  const currentQuestionOptions = (activityRef, field) => {
    const msg = [...(messages || [])].reverse().find(
      (m) => !m.answered
        && m.wizardMeta
        && m.wizardMeta.activityRef === activityRef
        && m.wizardMeta.field === field,
    )
    return (msg && msg.wizardMeta && msg.wizardMeta.options) || []
  }

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }

  // Mirrors ProgramMatrixItem#missing_field_labels' own grade levels + population check, so the
  // grades step only gets asked when it actually still needs something (an item with grades but
  // incomplete per-grade population counts still needs the step; one with both fully set doesn't).
  const hasCompleteGradesAndPopulation = (item) => {
    if (item.category === "Adult SEL") return true
    if (!item.grade_levels || item.grade_levels.length === 0) return false
    const grades = item.internal_selected_grades || []
    if (grades.length === 0) return false
    return grades.every(
      (g) => g.students_can_be_supported != null
        && g.students_being_supported != null,
    )
  }

  // Converts a raw incomplete_summary item into the same "activity" shape the document-
  // extraction flow uses, so it can walk through emitFieldQuestion/applyFieldValue/
  // nextFieldAfter/saveActivity unmodified. suggested_* carries the item's OWN current value
  // (not an AI suggestion) — document_* fields are deliberately left unset since there's no
  // document behind this activity at all; isExistingItem is what tells startActivity/
  // emitNextNeededQuestion to skip fields already filled in rather than re-confirming everything.
  // match.existing_item is a minimal { id } stub — enough for saveActivity's existing branch to
  // PUT straight to this item, never creating a new one.
  const activityFromExistingItem = (item) => ({
    client_ref: `existing-${item.id}`,
    isExistingItem: true,
    suggested_name:
      item.activity !== "Untitled activity" ? item.activity : null,
    suggested_description: item.description || null,
    suggested_category: item.category,
    suggested_tier_of_support: item.tier_of_support || null,
    suggested_length_of_time: item.length_of_time || null,
    suggested_data_collection: item.data_collection || null,
    suggested_grade_levels: item.grade_levels || [],
    suggested_students_can_be_supported: null,
    suggested_students_being_supported: null,
    suggested_grade_populations: {},
    suggested_sel_standards: item.sel_standards || {},
    hasCompleteGradesAndPopulation: hasCompleteGradesAndPopulation(item),
    match: {
      status: "matched",
      existing_item: { id: item.id },
      candidates: [],
      match_type: "existing",
      confidence: null,
      fields_with_conflict: [],
    },
  })

  // Whether `field` is already answered on an existing item, so emitNextNeededQuestion can skip
  // straight past it instead of re-asking something the matrix already has.
  const isExistingFieldPresent = (activity, field) => {
    if (field === "tier") return !!activity.suggested_tier_of_support
    if (field === "grades") return activity.hasCompleteGradesAndPopulation
    if (field === "years") return !!activity.suggested_length_of_time
    if (field === "name_description") return !!activity.suggested_name
    if (field === "description") return !!activity.suggested_description
    if (field === "data_collection") return !!activity.suggested_data_collection
    if (field === "indicators") return Object.keys(activity.suggested_sel_standards || {}).length > 0
    return false
  }

  // Carries an already-present field's existing value into collectedByRef without asking the
  // user about it — the field-specific assignments mirror applyFieldValue's own, just reading
  // from the activity's suggested_* value instead of a submitted payload.
  // `collected` is passed in explicitly (same reason as emitFieldQuestion's own note above) —
  // this runs in a tight loop within a single synchronous call (see emitNextNeededQuestion),
  // possibly several times before React ever re-renders, so reading collectedByRef state here
  // instead would silently miss whatever an EARLIER iteration in that same loop just carried
  // forward (e.g. domain from startActivity, then tier moments later) — each skipped field would
  // overwrite the others' progress instead of accumulating them.
  const applyPresentFieldValue = (activityRef, activity, field, collected) => {
    const next = { ...collected }
    if (field === "tier") next.tier = activity.suggested_tier_of_support
    if (field === "grades") {
      next.grades = activity.suggested_grade_levels || []
      next.internalSelectedGrades = []
    }
    if (field === "years") next.years = activity.suggested_length_of_time
    if (field === "name_description") next.name = activity.suggested_name
    if (field === "description") next.description = activity.suggested_description
    if (field === "data_collection") next.dataCollection = activity.suggested_data_collection
    if (field === "indicators") {
      next.indicators = Object.keys(activity.suggested_sel_standards || {})
      next.sel_standards = activity.suggested_sel_standards || {}
    }
    setCollectedByRef((prev) => ({ ...prev, [activityRef]: next }))
    return next
  }

  const nextFieldAfter = (field, collected) => {
    if (field === "domain") return "tier"
    // Adult SEL activities aren't tied to student grade levels at all, same as the Add
    // Activity form's own `category !== "Adult SEL"` gate around its grade-related fields.
    if (field === "tier") return collected.domain === "Adult SEL" ? "years" : "grades"
    if (field === "grades") return "years"
    if (field === "years") return "name_description"
    if (field === "name_description") return "description"
    if (field === "description") return "data_collection"
    if (field === "data_collection") return "indicators"
    if (field === "indicators") return "summary"
    return null
  }

  // `collected` is always passed in explicitly by the caller (rather than read from
  // collectedByRef state here) because setCollectedByRef is asynchronous — reading state
  // right after setting it would see the stale pre-update value (e.g. the summary step would
  // render without the field that was just answered).
  const emitFieldQuestion = (activity, field, collected) => {
    currentFieldRef.current = TYPED_INPUT_FIELDS.includes(field)
      ? { activityRef: activity.client_ref, field }
      : null
    switch (field) {
      case "domain":
        pushMessage(buildDomainQuestionMessage(activity))
        break
      case "tier":
        pushMessage(buildTierQuestionMessage(activity))
        break
      case "grades":
        pushMessage(buildGradesQuestionMessage(activity, collected.tier))
        break
      case "years":
        pushMessage(buildYearsQuestionMessage(activity))
        break
      case "name_description":
        pushMessage(buildNameDescriptionMessage(activity))
        break
      case "description":
        pushMessage(buildDescriptionQuestionMessage(activity))
        break
      case "data_collection":
        pushMessage(buildDataCollectionQuestionMessage(activity))
        break
      case "indicators":
        pushMessage(buildIndicatorsQuestionMessage(activity))
        break
      case "summary":
        pushMessage(
          buildSummaryMessage(
            activity,
            collected,
            extractedActivities.length - activityIndex - 1,
          ),
        )
        break
      default:
        break
    }
  }

  // For a document-extracted activity, just emits `field` as-is — every field is confirmed
  // regardless of whether GPT already suggested a value, since that confirmation IS the point
  // of that flow. For an existing-item activity (see activityFromExistingItem), walks FORWARD
  // through nextFieldAfter, silently carrying forward any field that's already present
  // (applyPresentFieldValue) instead of re-asking it, until it reaches either "summary" or the
  // next field that's genuinely still missing.
  const emitNextNeededQuestion = (activity, field, collected) => {
    let currentField = field
    let currentCollected = collected
    if (activity.isExistingItem) {
      while (
        currentField
        && currentField !== "summary"
        && isExistingFieldPresent(activity, currentField)
      ) {
        currentCollected = applyPresentFieldValue(
          activity.client_ref,
          activity,
          currentField,
          currentCollected,
        )
        currentField = nextFieldAfter(currentField, currentCollected)
      }
    }
    if (currentField) emitFieldQuestion(activity, currentField, currentCollected)
  }

  const startActivity = (index, activitiesOverride) => {
    // Every branch below either emits a new field question (which sets this itself) or shows a
    // non-field message (banner, confirm_match, done) — clearing it up front means those
    // non-field branches never leave the PREVIOUS activity's field stuck open for direct typing.
    currentFieldRef.current = null
    pendingPopulationRef.current = null
    // Only needed for the one caller (the "help me fill these in" button) that starts a brand
    // new activities queue and immediately calls startActivity(0, ...) in the same tick —
    // setExtractedActivities is async, so extractedActivities itself would still read the STALE
    // pre-update array at that exact moment without this override.
    const activities = activitiesOverride || extractedActivities
    const activity = activities[index]
    if (!activity) {
      // activityIndex + 1 running off the end does NOT mean every activity is actually done —
      // submitStartFreeText lets the user jump straight to any activity by number/name, so one
      // earlier in the list can still be genuinely untouched (never saved). Check for that FIRST,
      // before declaring victory.
      const pendingIndex = activities.findIndex(
        (a) => !savedActivityRefsRef.current.has(a.client_ref),
      )
      if (pendingIndex !== -1) {
        const pendingActivity = activities[pendingIndex]
        const pendingName = pendingActivity.isExistingItem
          ? (pendingActivity.suggested_name || "Untitled activity")
          : (pendingActivity.document_activity_name
            || (pendingActivity.suggested_name
              ? `${pendingActivity.suggested_name} (Rei suggests)`
              : "Untitled activity"))
        pushMessage(
          buildResumePendingActivityMessage(pendingIndex, activities.length, pendingName),
        )
        currentFieldRef.current = { activityRef: undefined, field: "resume_activity" }
        return
      }
      setStage(WIZARD_STAGE.DONE)
      if (isExistingItemModeRef.current) {
        isExistingItemModeRef.current = false
        pushMessage(
          buildTextMessage(
            "Nice work — that's everything filled in! You can upload another document any time.",
          ),
        )
        return
      }
      pushMessage(
        buildTextMessage(
          "All activities are done! You can upload another document any time.",
        ),
      )
      // One-time reminder about OTHER activities already on the matrix (not from this upload)
      // that are still missing required fields — informational only, no AI suggestions offered
      // for these (see buildIncompleteExistingActivitiesMessage).
      APIClient.get(
        "/program_matrix/program_matrix_items/incomplete_summary",
        (response) => {
          const incompleteByCategory = (response && response.incomplete_by_category) || {}
          incompleteByCategoryRef.current = incompleteByCategory
          if (Object.keys(incompleteByCategory).length > 0) {
            pushMessage(
              buildIncompleteExistingActivitiesMessage(incompleteByCategory),
            )
            // Opens this question to direct typing the same way emitFieldQuestion would for its
            // own fields — done manually here since it's pushed directly, and it's a matrix-wide
            // prompt with no activity to attach it to.
            currentFieldRef.current = { activityRef: undefined, field: "existing_incomplete_summary" }
          }
        },
        () => {},
      )
      return
    }
    setActivityIndex(index)

    if (activity.isExistingItem) {
      // Category is fixed on an existing item — never asked, just carried straight into
      // collected so nextFieldAfter's own domain-based branching (e.g. skipping grades for
      // Adult SEL) still works correctly.
      const startCollected = {
        ...emptyCollected(),
        domain: activity.suggested_category,
      }
      setCollectedByRef((prev) => ({
        ...prev,
        [activity.client_ref]: startCollected,
      }))
      pushMessage(
        buildActivityBannerMessage(
          index,
          activities.length,
          activity.suggested_name || "Untitled activity",
        ),
      )
      emitNextNeededQuestion(activity, "tier", startCollected)
      return
    }

    setCollectedByRef((prev) => ({
      ...prev,
      [activity.client_ref]: emptyCollected(),
    }))
    // Prefer the document's own name — falling straight to suggested_name here would silently
    // show Rei's own guess as if it were settled, contradicting the overview list's earlier
    // "No name in document" note for the exact same activity (buildFoundActivitiesMessage). The
    // actual name confirmation still happens in the very next step (buildNameDescriptionMessage);
    // this banner is just a heading, so it needs to flag the guess as a guess.
    const bannerName = activity.document_activity_name
      || (activity.suggested_name
        ? `${activity.suggested_name} (Rei suggests)`
        : "Untitled activity")
    pushMessage(
      buildActivityBannerMessage(index, activities.length, bannerName),
    )

    const isAmbiguous = activity.match
      && activity.match.status === "ambiguous"
      && (activity.match.candidates || []).length > 0
    if (isAmbiguous) {
      pushMessage(buildConfirmMatchMessage(activity))
    } else {
      emitFieldQuestion(activity, "domain", emptyCollected())
    }
  }

  const saveActivity = (activity, collected) => {
    setIsBusy(true)
    const itemParams = {
      activity: collected.name,
      description: collected.description,
      category: collected.domain,
      tier_of_support: collected.tier,
      length_of_time: collected.years,
      data_collection: collected.dataCollection,
      sel_standards: collected.sel_standards,
      internal_selected_grades: collected.internalSelectedGrades,
      is_completed: true,
      is_user_edited: true,
    }

    const putItem = (itemId) => {
      APIClient.put(
        `/program_matrix/program_matrix_items/${itemId}`,
        { program_matrix_item: itemParams },
        () => {
          setIsBusy(false)
          // ChatBotUI mounts in its own React root (see application.js), separate from
          // ProgramMatrix/Index's table — a window event is the only way to tell it to refetch.
          // eslint-disable-next-line no-console
          console.log("[programMatrixItemSaved] dispatching after save")
          window.dispatchEvent(new CustomEvent("programMatrixItemSaved"))
          savedActivityRefsRef.current.add(activity.client_ref)
          pushMessage(buildSuccessMessage(collected.name))
          // Not just activityIndex + 1 — if the user resumed an EARLIER pending activity out of
          // order (see the resume-activity check in startActivity), the one right after it may
          // already be saved from before. Skip forward past any already-saved activity; if
          // nothing forward is left, an out-of-bounds index correctly falls into startActivity's
          // OWN pending check (which also looks backward), rather than re-processing one that's
          // done.
          const nextIndex = extractedActivities.findIndex(
            (a, i) => i > activityIndex && !savedActivityRefsRef.current.has(a.client_ref),
          )
          startActivity(nextIndex === -1 ? extractedActivities.length : nextIndex)
        },
        () => {
          setIsBusy(false)
          message.error("Failed to save this activity. Please try again.")
        },
      )
    }

    const existingItem = activity.match && activity.match.existing_item
    if (existingItem) {
      putItem(existingItem.id)
    } else {
      APIClient.post(
        "/program_matrix/program_matrix_items",
        {
          program_matrix_item: {
            category: collected.domain,
            activity: collected.name,
          },
        },
        (created) => putItem(created.id),
        () => {
          setIsBusy(false)
          message.error("Failed to save this activity. Please try again.")
        },
      )
    }
  }

  const describeResponse = (field, payload) => {
    if (field === "start") return payload.label
    if (field === "confirm_match") return payload.label
    if (field === "domain") return payload.value || payload.label
    if (field === "tier") return payload.value
    if (field === "grades") return `Grades: ${(payload.grades || []).join(", ") || "None"}`
    if (field === "years") return payload.value
    if (field === "name_description") return payload.value || payload.label || "Yes, that's correct"
    // "Keep" options echo their own quoted value in the label (e.g. Keep "whattt") so the
    // button reads clearly, but echoing that same label back as the user's chat message would
    // show the word "Keep" as if the user typed it — echo just the value there instead. Other
    // description options (Yes, use this / Leave blank for now) keep the label-first behavior
    // so a long suggested description doesn't get dumped into the transcript as the "user" line.
    if (field === "description") {
      if (payload.label && payload.label.startsWith("Keep ")) return payload.value
      return payload.label || payload.value || "(left blank)"
    }
    if (field === "data_collection") return payload.label || payload.value
    if (field === "indicators") {
      return `Indicators: ${
        (payload.selectedCodes || []).join(", ") || "None"
      }`
    }
    if (field === "summary") return payload.action === "save" ? "Save to Matrix ✓" : "Make changes"
    return null
  }

  // Computes the updated collected-fields object synchronously (rather than deriving it inside
  // a setState updater) so the resulting value can be used immediately to decide the next step
  // and emit its question — a setState updater's return value can't safely drive side effects
  // like pushMessage, since React may invoke it more than once per render.
  const applyFieldValue = (activityRef, field, payload, freeTextValue) => {
    const existing = collectedByRef[activityRef] || emptyCollected()
    const next = { ...existing }

    // freeTextValue wins over payload.value for these three — payload is `{}` when the answer
    // came from the "type a different X" free-text option (see submitFreeText), so reading only
    // payload.value there would silently write `undefined` and lose the validated typed answer.
    if (field === "domain") next.domain = freeTextValue || payload.value
    if (field === "tier") next.tier = freeTextValue || payload.value
    if (field === "grades") {
      next.grades = payload.grades
      next.internalSelectedGrades = payload.internalSelectedGrades
    }
    if (field === "years") next.years = freeTextValue || payload.value
    if (field === "name_description") {
      const activity = extractedActivities.find(
        (a) => a.client_ref === activityRef,
      )
      // payload.value carries the actual chosen name string (document name or Rei's suggestion —
      // see buildNameDescriptionMessage), not a sentinel, so it must win over the activity default.
      next.name = freeTextValue || payload.value || (activity && activity.suggested_name)
    }
    // freeTextValue/payload.value can legitimately be "" (Leave blank for now / edited to empty),
    // so this can't fall back to the activity default the way name_description above does —
    // "" must stick, not silently get replaced by the AI's suggested_description.
    if (field === "description") next.description = freeTextValue !== undefined ? freeTextValue : payload.value
    if (field === "data_collection") next.dataCollection = freeTextValue || payload.value
    if (field === "indicators") {
      next.indicators = payload.selectedCodes
      next.sel_standards = payload.sel_standards
    }

    setCollectedByRef((prev) => ({ ...prev, [activityRef]: next }))
    return next
  }

  // A confirmed candidate is applied to `extractedActivities` (not just local state) so every
  // later step that reads `activity.match.existing_item` — tier, data_collection, indicators,
  // and the final save — treats this activity as the confirmed existing one without each of
  // those call sites needing their own awareness of the ambiguous-match flow.
  const confirmMatch = (activityRef, candidateId) => {
    setExtractedActivities((prev) => prev.map((a) => {
      if (a.client_ref !== activityRef) return a
      const candidate = (a.match.candidates || []).find(
        (c) => String(c.id) === candidateId,
      )
      return candidate
        ? {
          ...a,
          match: {
            ...a.match,
            existing_item: candidate,
            status: "matched",
          },
        }
        : a
    }))
  }

  // Document grades this school hasn't configured, and not yet resolved either way for this
  // activity — excludes anything already in collected.grades (included) AND collected.
  // excludedGrades (explicitly skipped). Checking BOTH is what keeps the one-at-a-time
  // unmatched-grades loop from re-asking about a grade the user already said "skip" to — a grade
  // only ever lands in `grades` when added, so without excludedGrades tracking a skipped grade
  // would look "still pending" forever and the loop would never terminate.
  const pendingDocumentGrades = (activity, collected) => {
    const validGrades = schoolGradeKeys()
    const documentGrades = activity.suggested_grade_levels || []
    return documentGrades.filter(
      (g) => !validGrades.includes(g)
        && !(collected.grades || []).includes(g)
        && !(collected.excludedGrades || []).includes(g),
    )
  }

  // Shared "how many can be supported" prompt for a grade's population sub-flow — used both to
  // kick the sub-flow off (maybeStartPopulationCollection) and to ask about the next queued grade
  // (finalizePopulationForGrade). A grade reaches this sub-flow either because it's genuinely
  // out-of-config (no real population to auto-fill from at all) or because it's a school-
  // configured grade that simply hasn't had its counts typed in yet — those need different
  // wording, or the "isn't part of your school's configured grade levels" framing would be
  // factually wrong for the second case.
  const buildPopulationCanQuestionText = (grade) => {
    const isConfiguredGrade = schoolGradeKeys().includes(grade)
    return isConfiguredGrade
      ? `You haven't given student counts for grade **${grade}** yet. How many students can be `
        + `supported in grade ${grade}?`
      : `Grade **${grade}** isn't part of your school's configured grade levels, so there's no `
        + `population to auto-fill from. How many students can be supported in grade ${grade}?`
  }

  // Kicks off the "collect population via chat" sub-flow (see pendingPopulationRef) for any grade
  // in `collected` that has no student counts yet — every grade typed via chat (see
  // submitGradeFreeText) is seeded with null counts up front specifically so it lands here, one
  // grade and one value at a time, regardless of whether it's school-configured or out-of-config.
  // Skipped for Universal (population is auto-filled from each grade's real configured total, so
  // there's nothing to ask — see submitGradeFreeText) and Adult SEL (no grades at all). Returns
  // true when it started asking — the caller should NOT also proceed to the next field yet.
  const maybeStartPopulationCollection = (activity, collected) => {
    if (collected.tier === "Universal" || collected.domain === "Adult SEL") return false
    const missingGrades = (collected.internalSelectedGrades || [])
      .filter((g) => g.students_can_be_supported == null || g.students_being_supported == null)
      .map((g) => g.grade)
    if (missingGrades.length === 0) return false
    const [firstGrade, ...rest] = missingGrades
    pendingPopulationRef.current = {
      activityRef: activity.client_ref,
      queue: rest,
      currentGrade: firstGrade,
      stage: "can",
      canValue: null,
    }
    currentFieldRef.current = { activityRef: activity.client_ref, field: "grade_population" }
    pushMessage(buildTextMessage(buildPopulationCanQuestionText(firstGrade)))
    return true
  }

  // The single place every grade-related step funnels through once its own immediate work is
  // done — a normal Submit, a typed grade match, or the "add another grade?" no answer. Population
  // for anything still missing counts always comes first (see maybeStartPopulationCollection), so
  // it's asked immediately after the grade that needs it was just decided. Once nothing's missing:
  // a document grade this school hasn't configured has no real population to track for it at all,
  // so it's automatically excluded and just announced (buildUnmatchedGradesInfoMessage) rather
  // than offered as something to force-add — see pendingDocumentGrades.
  const continueGradesFlow = (activity, collected) => {
    if (maybeStartPopulationCollection(activity, collected)) return
    const pendingGrades = pendingDocumentGrades(activity, collected)
    if (pendingGrades.length > 0) {
      const nextCollected = {
        ...collected,
        excludedGrades: [...(collected.excludedGrades || []), ...pendingGrades],
      }
      setCollectedByRef((prev) => ({ ...prev, [activity.client_ref]: nextCollected }))
      pushMessage(buildUnmatchedGradesInfoMessage(pendingGrades))
      const next = nextFieldAfter("grades", nextCollected)
      emitNextNeededQuestion(activity, next, nextCollected)
      return
    }
    const next = nextFieldAfter("grades", collected)
    emitNextNeededQuestion(activity, next, collected)
  }

  // Folds ONE grade into an already-collected `existing` (accumulating, not replacing) — needed
  // because each grade typed via chat is seeded and later finalized one at a time (see
  // submitGradeFreeText/finalizePopulationForGrade below), unlike the main grade_picker widget's
  // own Submit, whose payload.grades already reflects that widget's COMPLETE multi-select state in
  // one shot — advanceField's normal full-replace handling (applyFieldValue) is correct for that
  // caller, but would silently drop every earlier chat-confirmed grade the moment a later one came
  // through, if it were reused here too. A grade already present is never duplicated in `grades`,
  // but ITS COUNTS ARE UPDATED to whatever's passed now — it's typically sitting there with null
  // placeholders (folded in with nothing yet, or a rejected population answer that never
  // finalized), and a real value must actually take effect rather than being silently dropped,
  // or the wizard keeps re-asking about a grade the user just answered. Persists via
  // setCollectedByRef AND returns the updated value directly, since setCollectedByRef is
  // asynchronous — a caller that needs to act on the result immediately (continueGradesFlow)
  // can't safely re-read collectedByRef state right after calling this.
  const addGradeToCollected = (activityRef, existing, grade, canValue, beingValue) => {
    const alreadyPresent = (existing.grades || []).includes(grade)
    const next = {
      ...existing,
      grades: alreadyPresent ? existing.grades : [...(existing.grades || []), grade],
      // A grade already present gets its counts UPDATED, not skipped — it's typically sitting
      // there with null placeholders (e.g. folded in with nothing yet, or a rejected population
      // answer that never finalized), and a real value now (from the widget's own Submit, or a
      // later chat-typed count) must actually take, or the wizard keeps re-asking about a grade
      // whose counts the user just gave it (the exact bug this fixes) — see advanceField's
      // "grades" case, which calls this with a Submit's REAL counts for a grade that may already
      // be sitting here with nulls from an earlier fold-in.
      internalSelectedGrades: alreadyPresent
        ? (existing.internalSelectedGrades || []).map((g) => (
          g.grade === grade
            ? { ...g, students_can_be_supported: canValue, students_being_supported: beingValue }
            : g
        ))
        : [
          ...(existing.internalSelectedGrades || []),
          { grade, students_can_be_supported: canValue, students_being_supported: beingValue },
        ],
    }
    setCollectedByRef((prev) => ({ ...prev, [activityRef]: next }))
    return next
  }

  const markMessageAnswered = (messageIndex) => {
    setMessages((prev) => prev.map((m, i) => (i === messageIndex ? { ...m, answered: true } : m)))
  }

  // Same effect as markMessageAnswered, but keyed by the question's own (activityRef, field)
  // instead of its array index — needed for an answer that arrives via direct typing rather than
  // a button click, since that path never gets a messageIndex handed to it (see submitFreeText).
  // Without this, the original question's buttons stay clickable/unanswered on screen even after
  // a typed answer has already advanced the wizard past it.
  const markFieldQuestionAnswered = (activityRef, field) => {
    setMessages((prev) => prev.map((m) => (
      !m.answered
        && m.wizardMeta
        && m.wizardMeta.activityRef === activityRef
        && m.wizardMeta.field === field
        ? { ...m, answered: true }
        : m
    )))
  }

  // Shared between the button-click path (advanceField's own "grades_add_another" case) and the
  // typed-free-text path (submitGradesAddAnotherFreeText) — resolves the "Would you like to add
  // another grade?" prompt shown once a grade's population is fully captured (see
  // buildAddAnotherGradeMessage/finalizePopulationForGrade). "yes" re-opens the "grades" field to
  // typing so the same grade -> can-be-supported -> being-supported exchange can repeat for
  // whatever's typed next; "no" hands off to continueGradesFlow exactly as if population
  // collection itself had just finished, since — as far as the rest of the wizard is concerned —
  // it has.
  const resolveAddAnotherGrade = (activityRef, activity, value) => {
    if (value === "yes") {
      pushMessage(buildTextMessage("Which grade would you like to add?"))
      currentFieldRef.current = { activityRef, field: "grades" }
      return
    }
    currentFieldRef.current = null
    const collected = collectedByRef[activityRef] || emptyCollected()
    continueGradesFlow(activity, collected)
  }

  const advanceField = (activityRef, field, payload, freeTextValue) => {
    const activity = extractedActivities.find(
      (a) => a.client_ref === activityRef,
    )
    if (!activity) return

    if (field === "confirm_match") {
      if (payload.value !== "__new__") confirmMatch(activityRef, payload.value)
      emitFieldQuestion(activity, "domain", emptyCollected())
      return
    }

    if (field === "summary") {
      if (payload.action === "restart") {
        // An existing item's category is fixed and never re-asked (see startActivity), and
        // "restart" only redoes the fields this flow actually asked about, not ones it silently
        // carried forward — same skip behavior as the initial run, via emitNextNeededQuestion.
        const startCollected = activity.isExistingItem
          ? { ...emptyCollected(), domain: activity.suggested_category }
          : emptyCollected()
        setCollectedByRef((prev) => ({
          ...prev,
          [activityRef]: startCollected,
        }))
        if (activity.isExistingItem) {
          emitNextNeededQuestion(activity, "tier", startCollected)
        } else {
          emitFieldQuestion(activity, "domain", startCollected)
        }
      } else {
        saveActivity(activity, collectedByRef[activityRef] || emptyCollected())
      }
      return
    }

    if (field === "grades_add_another") {
      resolveAddAnotherGrade(activityRef, activity, payload.value)
      return
    }

    if (field === "grades") {
      // A grade_picker widget re-shown mid-flow (buildGradesProgressCardMessage, pushed after a
      // grade's population is captured via chat, or after an invalid answer — see
      // submitGradeFreeText/submitPopulationFreeText) pre-selects every grade already collected —
      // this activity already having some grades recorded BEFORE this Submit is what marks this as
      // that case (the only way that can happen mid-step is via addGradeToCollected; the MAIN
      // grades widget is never shown once any grades already exist, since existing-item pre-fill
      // skips the "grades" step entirely whenever it's already complete — see
      // isExistingFieldPresent). Its payload.grades must be ACCUMULATED onto whatever's already
      // collected instead of replacing it outright, unlike the MAIN grades widget's own Submit
      // (buildGradesQuestionMessage), whose payload.grades already IS the activity's complete
      // selection in a single shot — replacing is the correct behavior for THAT widget, since a
      // grade the user deselected there must actually disappear, not linger from a stale merge.
      const existing = collectedByRef[activityRef] || emptyCollected()
      const wasMidFlowCard = (existing.grades || []).length > 0
      let collected
      if (wasMidFlowCard) {
        // A "Would you like to add another grade?" prompt may still be sitting on screen
        // alongside this same card (see finalizePopulationForGrade) — submitting the card
        // directly answers that question implicitly, so it's marked answered here too, rather
        // than leaving it clickable and risking a second, conflicting "move to the next field"
        // once the user answers it too.
        markFieldQuestionAnswered(activityRef, "grades_add_another")
        const internalByGrade = (payload.internalSelectedGrades || []).reduce(
          (map, g) => ({ ...map, [g.grade]: g }),
          {},
        )
        collected = (payload.grades || []).reduce((acc, grade) => {
          const info = internalByGrade[grade] || {}
          return addGradeToCollected(
            activityRef,
            acc,
            grade,
            info.students_can_be_supported != null ? info.students_can_be_supported : null,
            info.students_being_supported != null ? info.students_being_supported : null,
          )
        }, existing)
      } else {
        collected = applyFieldValue(activityRef, field, payload, freeTextValue)
      }
      // Population for anything just included with no counts (see continueGradesFlow) is asked
      // about before any OTHER document grade GradePicker's own picker could never let the user
      // select (it only ever offers validGrades) — e.g. document says "1, 2" but this school only
      // has "1" configured — gets surfaced (as an informational left-out notice, not a choice —
      // see continueGradesFlow).
      continueGradesFlow(activity, collected)
      return
    }

    const collected = applyFieldValue(
      activityRef,
      field,
      payload,
      freeTextValue,
    )
    const next = nextFieldAfter(field, collected)
    emitNextNeededQuestion(activity, next, collected)
  }

  // Shared between the button-click path (onWizardRespond) and the typed-free-text path
  // (submitExistingIncompleteSummaryFreeText) — not tied to any activityRef (it's a matrix-wide
  // summary, not a per-activity question), so it's handled here rather than falling through to
  // advanceField, which requires one.
  const respondToExistingIncompleteSummary = (value) => {
    if (value === "help") {
      // Reuses the SAME extractedActivities/activityIndex/collectedByRef machinery the
      // document-extraction flow uses — the document run that just finished has no further use
      // for that state, so repurposing it here (rather than adding a second parallel queue) is
      // what lets startActivity/advanceField/saveActivity work for this flow unmodified.
      const items = Object.values(incompleteByCategoryRef.current).flat()
      const activities = items.map((item) => activityFromExistingItem(item))
      isExistingItemModeRef.current = true
      // A fresh batch — nothing in it has been saved yet, regardless of what a PREVIOUS batch's
      // savedActivityRefsRef already accumulated.
      savedActivityRefsRef.current = new Set()
      setExtractedActivities(activities)
      pushMessage(
        buildTextMessage("Sounds good — let's go through them one at a time."),
      )
      startActivity(0, activities)
    } else {
      pushMessage(
        buildTextMessage(
          "No problem — you can always come back to these from your Program Matrix whenever you're ready.",
        ),
      )
    }
  }

  // Shared between the button-click path (onWizardRespond) and the typed-free-text path
  // (submitTypoConfirmationFreeText) — resolves a "did you mean X?" prompt (see
  // buildTypoConfirmationMessage). "yes" applies the suggested value to the ORIGINAL field exactly
  // as if it had been typed correctly the first time (same advanceField call a normal free-text
  // answer makes); "no" re-asks that original question from scratch (re-showing its card) rather
  // than leaving the user with neither a valid answer nor a live set of buttons — the original
  // question's own message is left as-is (never marked answered), same as how the grade_population
  // sub-flow leaves its own triggering "grades" widget alone while asking follow-ups in parallel.
  const resolveTypoConfirmation = (value) => {
    const pending = pendingTypoConfirmationRef.current
    pendingTypoConfirmationRef.current = null
    currentFieldRef.current = null
    if (!pending) return
    markFieldQuestionAnswered(pending.activityRef, "typo_confirm")
    const activity = extractedActivities.find((a) => a.client_ref === pending.activityRef)
    if (!activity) return
    if (value === "yes") {
      markFieldQuestionAnswered(pending.activityRef, pending.field)
      advanceField(pending.activityRef, pending.field, {}, pending.suggestion)
    } else {
      const collected = collectedByRef[pending.activityRef] || emptyCollected()
      emitFieldQuestion(activity, pending.field, collected)
    }
  }

  // `payload.wizardField`/`wizardActivityRef` are injected by the ChatBotUI call site from the
  // message's own wizardMeta, so widgets themselves never need to know their field/activity.
  const onWizardRespond = (messageIndex, payload) => {
    markMessageAnswered(messageIndex)

    if (payload.wizardField === "start") {
      pushMessage({ role: "user", content: payload.label })
      startActivity(0)
      return
    }

    if (payload.wizardField === "resume_activity") {
      pushMessage({ role: "user", content: payload.label })
      startActivity(parseInt(payload.value, 10))
      return
    }

    if (payload.wizardField === "typo_confirm") {
      pushMessage({ role: "user", content: payload.label })
      resolveTypoConfirmation(payload.value)
      return
    }

    if (payload.wizardField === "existing_incomplete_summary") {
      pushMessage({ role: "user", content: payload.label })
      respondToExistingIncompleteSummary(payload.value)
      return
    }

    if (payload.freeText) {
      setAwaitingFreeText({
        activityRef: payload.wizardActivityRef,
        field: payload.wizardField,
      })
      pushMessage(
        buildTextMessage(
          "Sure — type your answer in the box below and hit send.",
        ),
      )
      return
    }

    const label = describeResponse(payload.wizardField, payload)
    if (label) pushMessage({ role: "user", content: label })
    advanceField(payload.wizardActivityRef, payload.wizardField, payload)
  }

  // Pushes a message (an error, or a notice like "that grade's already added") alongside a recap
  // of every grade already validly collected for this activity (see
  // buildGradesProgressCardMessage) — a rejected answer, or one that didn't actually change
  // anything, should never just wipe the slate from the user's view; whatever's already been added
  // stays visible so they can see the full picture while trying again.
  const pushGradeNoticeWithProgressCard = (activityRef, text) => {
    pushMessage(buildTextMessage(text))
    const activity = extractedActivities.find((a) => a.client_ref === activityRef)
    const existing = collectedByRef[activityRef] || emptyCollected()
    if (activity && (existing.grades || []).length > 0) {
      // A fresh card supersedes any earlier "grades" card for this activity — otherwise BOTH
      // stay live/editable at once (every message defaults to answered: false), and an edit typed
      // directly into the OLDER card (never Submitted, so collectedByRef never learns about it)
      // looks like it silently "reverted" the moment this NEW card — built fresh from
      // collectedByRef, with no knowledge of that unsubmitted edit — gets appended below it and
      // auto-scroll carries the user straight to it.
      markFieldQuestionAnswered(activityRef, "grades")
      pushMessage(buildGradesProgressCardMessage(activity, existing))
    }
  }

  // A typed grade must match either this activity's own document grades or this school's
  // configured dashboard grades — there is no third source a grade could legitimately come from.
  // Every matched grade (school-configured or out-of-config alike) is seeded into `collected` with
  // null counts and handed to continueGradesFlow, which — via maybeStartPopulationCollection —
  // walks through asking "how many can/being supported" for each one in turn, one grade and one
  // value at a time, the same chat-driven exchange an out-of-config document grade already used.
  // Universal activities skip that entirely: their population is always each grade's own real
  // configured total, auto-filled here directly (matching what GradePicker's own Submit computes
  // for a Universal activity), so there's nothing to ask about.
  const submitGradeFreeText = (activityRef, text) => {
    const activity = extractedActivities.find((a) => a.client_ref === activityRef)
    if (!activity) return true

    const validGrades = schoolGradeKeys()
    const documentGrades = activity.suggested_grade_levels || []
    const result = validateGradeFreeText(text, documentGrades, validGrades)

    if (!result.valid) {
      pushGradeNoticeWithProgressCard(activityRef, result.error)
      return true
    }

    markFieldQuestionAnswered(activityRef, "grades")
    const allGrades = [...result.dashboardGrades, ...result.documentOnlyGrades]

    let collected = collectedByRef[activityRef] || emptyCollected()
    // The FIRST time free text answers this activity's "grades" question, the still-open initial
    // widget (buildGradesQuestionMessage) has this document's own matched grades pre-checked on
    // screen, unsubmitted — GradePicker keeps that selection as local component state the wizard
    // never sees until Submit, so bypassing the widget with a typed grade must not silently
    // discard it: "1 and 3 pre-selected on the card, then type 'prek'" must end up with all
    // three, not just PreK. `collected.grades` is only ever empty here on this very first
    // interaction — every later one already has something collected (from an earlier typed grade
    // or a genuine Submit), so this never re-fires and never fights with a grade the user
    // deliberately excluded via the unmatched-grades flow.
    if (collected.grades.length === 0) {
      const preSelectedGrades = documentGrades.filter((g) => validGrades.includes(g))
      preSelectedGrades.forEach((grade) => {
        // Carries over whatever population the document already gave this grade (same values
        // the widget itself pre-fills) — "must be retained until the user edits it", not
        // discarded and re-asked about just because the widget got bypassed.
        const doc = documentPopulationForGrade(activity, grade)
        collected = addGradeToCollected(
          activityRef,
          collected,
          grade,
          doc.can_be_supported,
          doc.being_supported,
        )
      })
    }
    // A grade this activity already has must not be silently re-processed as "nothing to do" and
    // let the wizard fall through to advancing past the step (nothing would look "missing" since
    // it's already fully populated) — say so explicitly and keep asking, same as an invalid grade
    // does, rather than leaving the user wondering whether it registered at all.
    const alreadyAdded = allGrades.filter((grade) => (collected.grades || []).includes(grade))
    const newGrades = allGrades.filter((grade) => !(collected.grades || []).includes(grade))
    if (newGrades.length === 0) {
      const plural = alreadyAdded.length > 1
      pushGradeNoticeWithProgressCard(
        activityRef,
        `Grade${plural ? "s" : ""} **${alreadyAdded.join(", ")}** ${plural ? "are" : "is"} `
          + "already added. Which other grade would you like to add?",
      )
      return true
    }
    if (alreadyAdded.length > 0) {
      const plural = alreadyAdded.length > 1
      pushMessage(
        buildTextMessage(
          `Grade${plural ? "s" : ""} **${alreadyAdded.join(", ")}** ${plural ? "are" : "is"} `
            + "already added — I'll add the rest.",
        ),
      )
    }

    const isUniversal = collected.tier === "Universal"
    const populations = schoolGradePopulations()
    newGrades.forEach((grade) => {
      if (isUniversal) {
        const auto = populations[grade] || 0
        collected = addGradeToCollected(activityRef, collected, grade, auto, auto)
        return
      }
      // Same as the pre-selected fold above — a grade typed via chat can ALSO be one the
      // document already gave a population for (e.g. an out-of-config document grade with its
      // own per-grade breakdown), and that data must carry over rather than being ignored just
      // because it arrived through chat instead of the widget. Only for a grade the document
      // actually mentioned, though — documentPopulationForGrade's own aggregate-total fallback
      // is meant for a grade the document is genuinely talking about, not one the user is typing
      // in fresh (e.g. "PreK" when the document only ever discussed 1/2/3) — GradePicker's own
      // widget never applies that fallback outside its own suggestedGrades for the same reason.
      const doc = documentGrades.includes(grade)
        ? documentPopulationForGrade(activity, grade)
        : { can_be_supported: null, being_supported: null }
      collected = addGradeToCollected(
        activityRef,
        collected,
        grade,
        doc.can_be_supported,
        doc.being_supported,
      )
    })

    continueGradesFlow(activity, collected)
    return true
  }

  // Writes both counts for the currently-queued grade into collectedByRef, then either moves on
  // to the next queued grade's "can be supported" question or, once every pending grade in this
  // batch is done, shows the full recap card and asks whether to add another grade (see
  // buildGradesProgressCardMessage/buildAddAnotherGradeMessage/resolveAddAnotherGrade) rather than
  // silently advancing past this step.
  const finalizePopulationForGrade = (pending, activity, canValue, beingValue) => {
    const existing = collectedByRef[pending.activityRef] || emptyCollected()
    const updatedInternal = existing.internalSelectedGrades.map((g) => (
      g.grade === pending.currentGrade
        ? { ...g, students_can_be_supported: canValue, students_being_supported: beingValue }
        : g
    ))
    const collected = { ...existing, internalSelectedGrades: updatedInternal }
    setCollectedByRef((prev) => ({ ...prev, [pending.activityRef]: collected }))

    if (pending.queue.length > 0) {
      const [nextGrade, ...rest] = pending.queue
      pendingPopulationRef.current = {
        activityRef: pending.activityRef,
        queue: rest,
        currentGrade: nextGrade,
        stage: "can",
        canValue: null,
      }
      pushMessage(buildTextMessage(buildPopulationCanQuestionText(nextGrade)))
      return
    }

    pendingPopulationRef.current = null
    currentFieldRef.current = { activityRef: pending.activityRef, field: "grades_add_another" }
    // Supersedes any earlier "grades" card for this activity — see the identical note in
    // pushGradeNoticeWithProgressCard.
    markFieldQuestionAnswered(pending.activityRef, "grades")
    pushMessage(buildGradesProgressCardMessage(activity, collected))
    pushMessage(buildAddAnotherGradeMessage(pending.activityRef))
  }

  // Advances pendingPopulationRef's per-grade, per-count sub-flow one answer at a time: the
  // "can be supported" count for the current grade (capped at its real configured population when
  // this school actually has one — an out-of-config grade has none to cap against), then "being
  // supported" (capped at whatever "can be supported" just came back, same rule GradePicker's own
  // beingSupportedInvalid uses), then either the next queued grade's "can be supported" question
  // or — once every pending grade in this batch is done — the recap card + "add another grade?"
  // question (see finalizePopulationForGrade).
  //
  // Either stage's own number can arrive naming the OTHER field instead — "20 for being
  // supported" while "can be supported" is what's actually open, or "can be supported is 15"
  // while "being supported" is open — via detectPopulationFieldMention. A named mismatch is
  // stashed/applied as a correction rather than recorded as an answer to the field that was
  // actually asked, and the field genuinely still open keeps getting asked about until it's
  // actually answered. Any rejected answer re-displays the recap card alongside the error (see
  // pushGradeNoticeWithProgressCard) so grades already finalized earlier in this batch stay
  // visible.
  const submitPopulationFreeText = (text) => {
    const pending = pendingPopulationRef.current
    if (!pending) return true
    const activity = extractedActivities.find((a) => a.client_ref === pending.activityRef)
    if (!activity) {
      pendingPopulationRef.current = null
      currentFieldRef.current = null
      return true
    }

    const mentionedField = detectPopulationFieldMention(text)
    const max = schoolGradePopulations()[pending.currentGrade]

    if (pending.stage === "can") {
      if (mentionedField === "being") {
        const result = validatePopulationCount(text, max, pending.currentGrade)
        if (!result.valid) {
          pushGradeNoticeWithProgressCard(pending.activityRef, result.error)
          return true
        }
        pendingPopulationRef.current = { ...pending, pendingBeingValue: result.value }
        pushMessage(buildTextMessage(
          `Got it — I'll use ${result.value} for being supported. How many students CAN be `
            + `supported in grade ${pending.currentGrade}?`,
        ))
        return true
      }

      const result = validatePopulationCount(text, max, pending.currentGrade)
      if (!result.valid) {
        pushGradeNoticeWithProgressCard(pending.activityRef, result.error)
        return true
      }

      if (pending.pendingBeingValue != null) {
        if (pending.pendingBeingValue > result.value) {
          pushGradeNoticeWithProgressCard(
            pending.activityRef,
            `That's less than the ${pending.pendingBeingValue} you already gave for being `
              + "supported — being supported can't exceed can be supported. Please enter a "
              + `number of at least ${pending.pendingBeingValue}.`,
          )
          return true
        }
        finalizePopulationForGrade(pending, activity, result.value, pending.pendingBeingValue)
        return true
      }

      pendingPopulationRef.current = { ...pending, stage: "being", canValue: result.value }
      pushMessage(buildTextMessage(
        `And how many of those ${result.value} are currently being supported in grade `
          + `${pending.currentGrade}?`,
      ))
      return true
    }

    if (mentionedField === "can") {
      // Read as a correction to the earlier can_be_supported answer, not a being_supported
      // count — being supported is still genuinely unanswered, so it's asked again below.
      const result = validatePopulationCount(text, max, pending.currentGrade)
      if (!result.valid) {
        pushGradeNoticeWithProgressCard(pending.activityRef, result.error)
        return true
      }
      pendingPopulationRef.current = { ...pending, canValue: result.value }
      pushMessage(buildTextMessage(
        `Got it — updating can be supported to ${result.value}. And how many are currently `
          + `being supported in grade ${pending.currentGrade}?`,
      ))
      return true
    }

    const result = validatePopulationCount(text, pending.canValue, pending.currentGrade)
    if (!result.valid) {
      pushGradeNoticeWithProgressCard(pending.activityRef, result.error)
      return true
    }
    finalizePopulationForGrade(pending, activity, pending.canValue, result.value)
    return true
  }

  // Lets a typed answer to the "Start with Activity 1 →" prompt (field: "start") OR the
  // "Activity N ... is still pending" resume prompt (field: "resume_activity" — see
  // buildResumePendingActivityMessage) name a DIFFERENT activity — by its number ("activity 2",
  // "start with activity 3", a bare "2") OR by the name itself (e.g. "social communication",
  // matched against document_activity_name — the same text buildFoundActivitiesMessage's numbered
  // list showed the user, so they can type back exactly what they read) — instead of only ever
  // being able to start whichever ONE activity that prompt's own button offers. Routes through the
  // exact same startActivity() the button uses, so the resulting banner + first field question is
  // identical either way, just for a different activity. A bare affirmative ("yes"/"ready"/etc,
  // naming no specific activity) defaults to Activity 1 for the "start" prompt, but to the
  // SPECIFIC pending activity that prompt is about for "resume_activity" — recomputed fresh here
  // rather than threaded through as an argument, since it's cheap and always still accurate at the
  // moment the user actually answers.
  const submitStartFreeText = (field, text) => {
    const trimmed = text.trim()
    const lowerTrimmed = trimmed.toLowerCase()

    const selectActivity = (index) => {
      // A name/number typed here can point at an activity that's ALREADY been saved this session
      // (e.g. re-typing the one just finished instead of the one the resume prompt is actually
      // about) — re-running startActivity on it would reset its answers and, on Save, could
      // create a genuine DUPLICATE matrix item rather than harmlessly updating the same one.
      // Refuse and let them try again instead; the question stays open (currentFieldRef untouched).
      const target = extractedActivities[index]
      if (target && savedActivityRefsRef.current.has(target.client_ref)) {
        pushMessage(
          buildTextMessage(
            `Activity ${index + 1} has already been saved — which OTHER activity would you like `
              + "to start?",
          ),
        )
        return
      }
      markFieldQuestionAnswered(undefined, field)
      currentFieldRef.current = null
      startActivity(index)
    }

    const defaultIndex = field === "start"
      ? 0
      : extractedActivities.findIndex((a) => !savedActivityRefsRef.current.has(a.client_ref))

    // Only activities the document itself named are matchable this way at all — an unnamed one
    // ("No name in document...") has nothing for a typed name to match against, so it's only ever
    // reachable by number.
    const namedActivities = extractedActivities
      .map((activity, index) => ({ activity, index }))
      .filter(({ activity }) => !!activity.document_activity_name)

    const exactNameMatches = namedActivities.filter(
      ({ activity }) => activity.document_activity_name.toLowerCase() === lowerTrimmed,
    )
    if (exactNameMatches.length === 1) {
      selectActivity(exactNameMatches[0].index)
      return true
    }

    // Falls back to a partial match the same way validateFreeTextAnswer's matchAgainstList does
    // for domain/tier/years — e.g. typing just "communication" for a full document name of
    // "Communication Skills Practice" — but only when EXACTLY one activity's name contains (or is
    // contained by) what was typed; two or more is genuine ambiguity, not a shorthand, so it asks
    // rather than guesses which one was meant.
    const partialNameMatches = namedActivities.filter(({ activity }) => {
      const name = activity.document_activity_name.toLowerCase()
      return name.includes(lowerTrimmed) || lowerTrimmed.includes(name)
    })
    if (partialNameMatches.length === 1) {
      selectActivity(partialNameMatches[0].index)
      return true
    }
    if (partialNameMatches.length > 1) {
      const names = partialNameMatches.map(({ activity }) => activity.document_activity_name)
      pushMessage(
        buildTextMessage(
          `"${trimmed}" could mean more than one activity (${names.join(", ")}) — please type `
            + "the full name, or its number.",
        ),
      )
      return true
    }

    const numberMatch = trimmed.match(/\d+/)
    if (!numberMatch) {
      if (AFFIRMATIVE_START_WORDS.includes(lowerTrimmed) && defaultIndex !== -1) {
        selectActivity(defaultIndex)
        return true
      }
      pushMessage(
        buildTextMessage(
          "Let me know which activity you'd like to start with — its name or number (e.g. "
            + "\"activity 2\") — or click the button above.",
        ),
      )
      return true
    }

    const index = parseInt(numberMatch[0], 10) - 1
    if (index < 0 || index >= extractedActivities.length) {
      pushMessage(
        buildTextMessage(
          `I only found ${extractedActivities.length} activit${
            extractedActivities.length === 1 ? "y" : "ies"
          } in this document — please pick a number between 1 and ${extractedActivities.length}.`,
        ),
      )
      return true
    }

    selectActivity(index)
    return true
  }

  // Same idea again, for the one-time "you also have N incomplete activities — help fill these
  // in, or do it yourself?" prompt (buildIncompleteExistingActivitiesMessage) — shared with
  // onWizardRespond's button-click path via respondToExistingIncompleteSummary.
  const submitExistingIncompleteSummaryFreeText = (text) => {
    const intent = matchBinaryIntent(
      text,
      ["help", "yes", "please"],
      ["myself", "no", "later", "not now"],
    )
    if (!intent) {
      pushMessage(
        buildTextMessage(
          "Let me know if you'd like help filling these in, or click a button above.",
        ),
      )
      return true
    }
    markFieldQuestionAnswered(undefined, "existing_incomplete_summary")
    currentFieldRef.current = null
    respondToExistingIncompleteSummary(intent === "positive" ? "help" : "myself")
    return true
  }

  // Same idea again, for the final "Here's the complete activity — ready to save?" summary card
  // (buildSummaryMessage) — SummaryCard's own buttons carry an `action` of "save" or "restart",
  // which this maps a typed reply onto the same way.
  const submitSummaryFreeText = (activityRef, text) => {
    const intent = matchBinaryIntent(
      text,
      ["save", "yes", "looks good", "confirm", "correct"],
      ["restart", "edit", "change", "redo", "no"],
    )
    if (!intent) {
      pushMessage(
        buildTextMessage(
          "Let me know if you'd like to save this activity or make changes, or use the buttons above.",
        ),
      )
      return true
    }
    markFieldQuestionAnswered(activityRef, "summary")
    currentFieldRef.current = null
    advanceField(activityRef, "summary", { action: intent === "positive" ? "save" : "restart" })
    return true
  }

  // Same idea again, for the "did you mean X?" typo-confirmation prompt
  // (buildTypoConfirmationMessage) — shared with onWizardRespond's button-click path via
  // resolveTypoConfirmation.
  const submitTypoConfirmationFreeText = (text) => {
    const intent = matchBinaryIntent(
      text,
      ["yes", "yeah", "yep", "correct", "right", "use it"],
      ["no", "nope", "wrong", "different"],
    )
    if (!intent) {
      pushMessage(buildTextMessage("Let me know yes or no, or use the buttons above."))
      return true
    }
    resolveTypoConfirmation(intent === "positive" ? "yes" : "no")
    return true
  }

  // Answers the "Would you like to add another grade?" prompt (buildAddAnotherGradeMessage) shown
  // once a grade's population is fully captured. A directly-typed grade at this point is read as
  // an implicit "yes" — skips the extra "which grade?" round-trip when the user just names it
  // right away, same forgiving pattern submitStartFreeText uses for a bare activity number/name.
  const submitGradesAddAnotherFreeText = (activityRef, text) => {
    const activity = extractedActivities.find((a) => a.client_ref === activityRef)
    if (!activity) return true

    const validGrades = schoolGradeKeys()
    const documentGrades = activity.suggested_grade_levels || []
    const gradeResult = validateGradeFreeText(text, documentGrades, validGrades)
    if (gradeResult.valid) {
      markFieldQuestionAnswered(activityRef, "grades_add_another")
      currentFieldRef.current = null
      return submitGradeFreeText(activityRef, text)
    }

    const intent = matchBinaryIntent(
      text,
      ["yes", "sure", "add", "ok", "okay"],
      ["no", "done", "stop", "finish", "that's all"],
    )
    if (!intent) {
      pushMessage(
        buildTextMessage(
          "Let me know if you'd like to add another grade, or use the buttons above.",
        ),
      )
      return true
    }
    markFieldQuestionAnswered(activityRef, "grades_add_another")
    resolveAddAnotherGrade(activityRef, activity, intent === "positive" ? "yes" : "no")
    return true
  }

  // The SEL indicators checklist (buildIndicatorsQuestionMessage) is a genuine multi-select
  // widget — each code carries its own connection-type/explanation sub-state, not a single value
  // — so there's no safe way to interpret arbitrary typed text as "select this code" the way a
  // simple yes/no or single-value field can. Rather than either guessing at that (risking picking
  // the wrong indicator) or leaving it unhandled (which would silently fall through to the
  // generic GPT chat endpoint and produce a fabricated, unrelated set of indicators — the exact
  // bug this fixes), this always redirects back to the real checklist above. The question stays
  // open (currentFieldRef untouched) so the buttons remain the way to actually answer it.
  const submitIndicatorsFreeText = () => {
    pushMessage(
      buildTextMessage(
        "Please use the checklist above — check the indicators you want, then click "
          + "\"+ Add another indicator\" or \"Confirm indicators\" to continue.",
      ),
    )
    return true
  }

  const submitFreeText = (text) => {
    // awaitingFreeText (set by clicking a "type a different X" button) and currentFieldRef (set
    // for ANY open free-text-eligible question, clicked or not — see emitFieldQuestion) resolve
    // to the same target field either way; checking both means the user can just type an answer
    // straight into the box without clicking a button first, same as clicking it would.
    const target = awaitingFreeText || currentFieldRef.current
    if (!target) return false

    pushMessage({ role: "user", content: text })
    const { activityRef, field } = target

    if (field === "grades") return submitGradeFreeText(activityRef, text)
    if (field === "grade_population") return submitPopulationFreeText(text)
    if (field === "start") return submitStartFreeText("start", text)
    if (field === "resume_activity") return submitStartFreeText("resume_activity", text)
    if (field === "existing_incomplete_summary") return submitExistingIncompleteSummaryFreeText(text)
    if (field === "summary") return submitSummaryFreeText(activityRef, text)
    if (field === "typo_confirm") return submitTypoConfirmationFreeText(text)
    if (field === "grades_add_another") return submitGradesAddAnotherFreeText(activityRef, text)
    if (field === "indicators") return submitIndicatorsFreeText()

    if (field === "name_description" || field === "description" || field === "data_collection") {
      const matched = matchOptionByLabel(currentQuestionOptions(activityRef, field), text)
      if (matched) {
        markFieldQuestionAnswered(activityRef, field)
        setAwaitingFreeText(null)
        currentFieldRef.current = null
        advanceField(activityRef, field, { value: matched.value, label: matched.label })
        return true
      }
    }

    // Invalid input re-prompts for the SAME field instead of advancing — neither ref/state is
    // cleared, so the next thing the user types is routed back through this same check (see
    // ChatBotUI's handleSend / isAwaitingFreeText), rather than silently falling through to the
    // generic GPT chat flow or advancing on a value the backend would reject on save.
    const result = validateFreeTextAnswer(field, text)
    if (!result.valid) {
      // A near-miss (e.g. "behavior supprt" for "Behavior Supports") gets offered back as a
      // yes/no confirmation instead of a flat rejection — see matchAgainstList's fuzzy fallback
      // and resolveTypoConfirmation above.
      if (result.suggestion) {
        pendingTypoConfirmationRef.current = { activityRef, field, suggestion: result.suggestion }
        currentFieldRef.current = { activityRef, field: "typo_confirm" }
        pushMessage(buildTypoConfirmationMessage(activityRef, field, text, result.suggestion))
        return true
      }
      pushMessage(buildTextMessage(result.error))
      return true
    }

    // matchAgainstList (inside validateFreeTextAnswer) accepts a partial match — "target" for
    // "Targeted" — so result.value can genuinely differ from what was typed. A button click's own
    // label already shows exactly what was chosen; a typed answer has no equivalent unless this
    // says so explicitly, so silently advancing straight to the next question would leave no way
    // to tell whether "target" was understood as "Targeted" or something else entirely.
    if (result.value.toLowerCase() !== text.trim().toLowerCase()) {
      pushMessage(buildTextMessage(`Got it — **${result.value}**.`))
    }

    markFieldQuestionAnswered(activityRef, field)
    setAwaitingFreeText(null)
    currentFieldRef.current = null
    advanceField(activityRef, field, {}, result.value)
    return true
  }

  const pollExtraction = (extractionId) => {
    activeExtractionIdRef.current = extractionId
    // The interval can have a request in flight when the next tick fires (slow response, or
    // timing lands right on the edge). If two responses both come back "completed"/"failed",
    // stopPolling() from the first only clears the interval — it can't cancel the second
    // request, whose callback would otherwise still run and push every message a second time.
    // Once stopPolling() nulls pollRef.current, treat later-arriving responses as stale.
    //
    // pollRef.current === null alone isn't enough, though: if a NEW document is attached while
    // an OLD extraction's request is still in flight, handleFileAttach's stopPolling() clears
    // pollRef.current, but a fresh pollExtraction() call for the NEW extraction then immediately
    // reassigns it to a new (non-null) interval — so by the time the OLD in-flight response
    // arrives, pollRef.current looks "active" again and this check alone would wrongly accept it
    // as current, overwriting the new extraction's activities with the old document's. Comparing
    // extractionId against activeExtractionIdRef (always the most recently started extraction)
    // catches that case even though pollRef.current is non-null.
    pollRef.current = setInterval(() => {
      APIClient.get(
        `/program_matrix/program_matrix_activity_extractions/${extractionId}`,
        (response) => {
          if (
            pollRef.current === null
            || activeExtractionIdRef.current !== extractionId
          ) return

          if (response.status === "completed") {
            stopPolling()
            setIsBusy(false)
            // Every extracted activity always gets saved as its own new Program Matrix item —
            // never silently updates an existing one just because the name matches (confirmed
            // requirement: a name collision should still produce a separate activity, not a
            // merge). Neutralizing match here, right where extraction results first arrive, means
            // isAmbiguous (startActivity), the keep-or-accept vs. plain checklist branch
            // (buildIndicatorsQuestionMessage), and the create-vs-update decision (saveActivity)
            // all fall through to their "no match" behavior without each needing its own check.
            const activities = (response.extracted_activities || []).map(
              (a, i) => ({
                ...a,
                client_ref: a.client_ref || `extract-${i}`,
                match: {
                  status: "no_match",
                  match_type: null,
                  confidence: null,
                  existing_item: null,
                  candidates: [],
                  fields_with_conflict: [],
                },
              }),
            )
            setExtractedActivities(activities)
            setStage(WIZARD_STAGE.ACTIVITY_FLOW)
            if (activities.length === 0) {
              pushMessage(
                buildTextMessage(
                  "I couldn't find any activities described in that document. Try uploading a different file.",
                ),
              )
              return
            }
            if (response.truncated) {
              pushMessage(
                buildTextMessage(
                  "This document is long, so I may have only found some of the activities in it.",
                ),
              )
            }
            pushMessage(buildFoundActivitiesMessage(activities))
            pushMessage(buildStartPromptMessage())
            // Opens this question to direct typing (e.g. "activity 2") the same way
            // emitFieldQuestion would — done manually here since this message is pushed directly
            // rather than through emitFieldQuestion (there's no "activity" to attach it to; it's
            // a matrix-wide prompt).
            currentFieldRef.current = { activityRef: undefined, field: "start" }
          } else if (response.status === "failed") {
            stopPolling()
            setIsBusy(false)
            pushMessage(buildErrorMessage(response.error))
          }
        },
        () => {
          if (
            pollRef.current === null
            || activeExtractionIdRef.current !== extractionId
          ) return
          stopPolling()
          setIsBusy(false)
          pushMessage(buildErrorMessage())
        },
      )
    }, POLL_INTERVAL_MS)
  }

  const handleFileAttach = (file) => {
    if (!isValidFileType(file)) {
      pushMessage({
        role: "user",
        content: file.name,
        fileAttachment: { name: file.name, size: file.size },
      })
      pushMessage(buildErrorMessage(
        `"${file.name}" is not a supported file type. Please upload a CSV, XLS, or XLSX file.`,
      ))
      return
    }

    // A new upload replaces extractedActivities/collectedByRef entirely — any field question
    // still open from the PREVIOUS document is no longer answerable, so direct typing shouldn't
    // keep being routed to it.
    currentFieldRef.current = null
    pendingPopulationRef.current = null
    // A fresh batch — nothing in it has been saved yet, regardless of what the PREVIOUS document's
    // savedActivityRefsRef already accumulated.
    savedActivityRefsRef.current = new Set()
    stopPolling()
    // Lock any question left over from a prior document — extractedActivities/collectedByRef
    // get replaced for the new upload, so answering a stale card would act on the wrong activity.
    setMessages((prev) => prev.map((m) => (m.type && !m.answered ? { ...m, answered: true } : m)))
    pushMessage({
      role: "user",
      content: file.name,
      fileAttachment: { name: file.name, size: file.size },
    })
    pushMessage(buildAnalyzingMessage())
    setStage(WIZARD_STAGE.ANALYZING)
    setIsBusy(true)

    const formData = new FormData()
    formData.append("file", file)

    APIClient.post(
      "/program_matrix/program_matrix_activity_extractions",
      formData,
      (response) => pollExtraction(response.id),
      (errorResponse) => {
        setIsBusy(false)
        pushMessage(buildErrorMessage(errorResponse && errorResponse.error))
      },
    )
  }

  return {
    stage,
    handleFileAttach,
    onWizardRespond,
    submitFreeText,
    isBusy,
    // True whenever ChatBotUI's handleSend should route typed input into submitFreeText instead
    // of the generic GPT chat flow — either because the user clicked a "type a different X"
    // button (awaitingFreeText) or because a free-text-eligible question is simply on screen and
    // unanswered (currentFieldRef), so a direct typed answer is understood without that click.
    isAwaitingFreeText: !!awaitingFreeText || !!currentFieldRef.current,
  }
}

export default useProgramMatrixWizard
